import React, { useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { Button } from "components/common/Button";
import { Callout } from "components/common/Callout";
import { InlineError } from "components/common/InlineError";
import { SectionRule } from "components/common/SectionRule";
import { Spinner } from "components/common/Spinner";
import { TooltipButton } from "components/common/TooltipButton";
import { plural } from "components/common/setUtils";
import { MoveTableDialog } from "@/components/canvas/detailsSide/utils/ownership/MoveTableDialog";
import { CardFormDialog } from "components/main/organizations/CardFormDialog";
import { OrgMark } from "@/components/main/organizations/OrgMark";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { isEnded, mintCardTransaction } from "@/lib/api/organizations";
import { formatDayMonthTime, formatShortDayMonth } from "@/lib/dates";
import { useAuth } from "contexts/AuthProvider";
import { axiosInstance } from "contexts/axiosApiInstance";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { CriticalTableRow } from "./CriticalTableRow";
import { useDeleteAccount, type TableWithModel } from "./useDeleteAccount";

/// ACCOUNT_DELETION_GRACE_PERIOD_SECS and the D42 copy window, server-side.
const GRACE_MS = 48 * 3_600_000;
const COPY_WINDOW_MS = 10 * 86_400_000;

interface OrgRef {
  org_id: string;
  name: string;
}

const listFormat = new Intl.ListFormat("en", {
  style: "long",
  type: "conjunction",
});

/// One org the account's card pays for, with the inline card form on it.
const BilledOrgRow: React.FC<{ org: OrgRef }> = ({ org }) => {
  const [busy, setBusy] = useState(false);
  const [cardTransaction, setCardTransaction] = useState<string | null>(null);
  const openCardForm = async () => {
    setBusy(true);
    const transaction = await mintCardTransaction(org.org_id);
    setBusy(false);
    if (isEnded(transaction)) {
      showNotificationGlobal(`${org.name}'s subscription has already ended.`);
      return;
    }
    if (!transaction) {
      showNotificationGlobal(
        `The card form for ${org.name} could not be opened.`,
        "error",
      );
      return;
    }
    setCardTransaction(transaction);
  };

  return (
    <span className="flex w-full items-center justify-between gap-2">
      <span className="text-fg-medium">{org.name}</span>
      <Button
        variant="secondary"
        size="sm"
        disabled={busy}
        onClick={() => void openCardForm()}
        data-testid={`delete-account-update-card-${org.org_id}`}
      >
        Update card
      </Button>
      <CardFormDialog
        transactionId={cardTransaction}
        onClose={() => setCardTransaction(null)}
        onCompleted={() => setCardTransaction(null)}
        onSettled={() => undefined}
      />
    </span>
  );
};

const TimelineRow: React.FC<{
  marker: "now" | "purge" | "later" | "stays";
  when: string;
  children: React.ReactNode;
}> = ({ marker, when, children }) => (
  <li className="deletion-timeline-row" data-marker={marker}>
    <span className="deletion-timeline-mark" aria-hidden />
    <span className="deletion-timeline-when">{when}</span>
    <span className="text-fg-regular">{children}</span>
  </li>
);

interface DeleteAccountViewProps {
  /// Loads the analysis while shown.
  active: boolean;
  onBack: () => void;
  onScheduled: () => void;
}

/// D55: the press puts the personal org, its spaces and models in the Trash
/// for 48 hours; then its tables go, and anyone who imported one has 10 more
/// days to copy it. The one pre-emptive act is moving a table into a model in a
/// space someone else manages too.
export const DeleteAccountView: React.FC<DeleteAccountViewProps> = ({
  active,
  onBack,
  onScheduled,
}) => {
  const { setAccountStatus } = useAuth();
  const orgs = useOrgContextStore((s) => s.orgs);
  const refreshOrgs = useOrgContextStore((s) => s.refreshOrgs);
  const [isDeleting, setIsDeleting] = useState(false);
  const [movingTable, setMovingTable] = useState<TableWithModel | null>(null);
  // A co-owner can leave between the analysis and the press; the 409 names
  // the orgs the same way the analysis does.
  const [refusedOver, setRefusedOver] = useState<OrgRef[] | null>(null);
  const { loading, error, analysis, allTables, criticalTables, refresh } =
    useDeleteAccount(active);

  const soleOwned = refusedOver ?? analysis?.sole_owned_orgs ?? [];
  const companyOrgs = orgs.filter((o) => !o.is_personal).map((o) => o.name);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axiosInstance.post("/action/delete_account", {});
      setAccountStatus("scheduledfordeletion");
      void refreshOrgs();
      showNotificationGlobal(
        "Account scheduled for deletion. You have 48 hours to cancel.",
        "success",
      );
      onScheduled();
    } catch (err) {
      const data = (
        err as {
          response?: { data?: { reason?: string; organizations?: OrgRef[] } };
        }
      )?.response?.data;
      if (data?.reason === "sole_org_owner" && data.organizations) {
        setRefusedOver(data.organizations);
      } else {
        showNotificationGlobal("Failed to schedule account deletion", "error");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const now = Date.now();
  const modelCount = analysis?.models.length ?? 0;

  return (
    <>
      <div className="settings-delete-head">
        <TooltipButton
          title="Back"
          aria-label="Back to settings"
          variant="default"
          size="sm"
          onClick={onBack}
        >
          <ArrowBackIcon />
        </TooltipButton>
        <h2 className="text-fg-title">Delete account</h2>
      </div>

      {loading || !analysis ? (
        <div className="flex justify-center py-8">
          {error ? <InlineError>{error}</InlineError> : <Spinner />}
        </div>
      ) : (
        <>
          <ol className="deletion-timeline" aria-label="What happens">
            <TimelineRow marker="now" when="Today">
              {modelCount > 0
                ? `Your organization, its spaces and ${plural(modelCount, "model")} go to the Trash`
                : "Your organization and its spaces go to the Trash"}
            </TimelineRow>
            <TimelineRow
              marker="purge"
              when={formatDayMonthTime(now + GRACE_MS)}
            >
              {allTables.length > 0
                ? `${plural(allTables.length, "table")} deleted for good`
                : "Deleted for good"}
              <span className="text-fg-muted"> · cancel until then</span>
            </TimelineRow>
            {criticalTables.length > 0 && (
              <TimelineRow
                marker="later"
                when={formatShortDayMonth(now + GRACE_MS + COPY_WINDOW_MS)}
              >
                {plural(criticalTables.length, "table")} others imported can no
                longer be copied
              </TimelineRow>
            )}
            {companyOrgs.length > 0 && (
              <TimelineRow marker="stays" when="Stays">
                Models in {listFormat.format(companyOrgs)}
              </TimelineRow>
            )}
          </ol>

          {soleOwned.length > 0 && (
            <section aria-label="Hand over first">
              <SectionRule label="Hand over first" />
              {soleOwned.map((o) => {
                const known = orgs.find((k) => k.org_id === o.org_id);
                return (
                  <div key={o.org_id} className="settings-row">
                    <OrgMark
                      org={{ ...o, has_image: !!known?.has_image }}
                      size="sm"
                    />
                    <span className="min-w-0 flex-1 truncate text-fg-regular">
                      {o.name}
                    </span>
                    <span className="text-fg-muted-regular-small text-error">
                      Only owner
                    </span>
                    {/* A new tab: the fix happens there, and this flow waits. */}
                    <TooltipButton
                      title="Open settings in a new tab"
                      aria-label={`Open settings for ${o.name} in a new tab`}
                      variant="contextual"
                      size="xs"
                      onClick={() =>
                        window.open(
                          `/organizations/${o.org_id}`,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      <OpenInNewIcon />
                    </TooltipButton>
                  </div>
                );
              })}
            </section>
          )}

          {analysis.orgs_you_pay_for.length > 0 && (
            <Callout
              variant="warning"
              title={
                analysis.orgs_you_pay_for.length === 1
                  ? "You pay for an organization that stays behind"
                  : "You pay for organizations that stay behind"
              }
            >
              <span className="flex flex-col items-start gap-2">
                <span>
                  Deleting your account does not cancel these subscriptions.
                  Replace the card while you are still an owner.
                </span>
                {analysis.orgs_you_pay_for.map((o) => (
                  <BilledOrgRow key={o.org_id} org={o} />
                ))}
              </span>
            </Callout>
          )}

          {criticalTables.length > 0 && (
            <section aria-label="Imported by others">
              <SectionRule
                label="Imported by others"
                count={criticalTables.length}
              />
              <div className="settings-delete-tables">
                {criticalTables.map((table) => (
                  <CriticalTableRow
                    key={table.table_id}
                    table={table}
                    onMove={() => setMovingTable(table)}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <div className="settings-delete-actions">
        {soleOwned.length > 0 && (
          <span className="mr-auto text-fg-muted-regular-small">
            Add another owner first
          </span>
        )}
        <Button variant="secondary" onClick={onBack}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={() => void handleDelete()}
          loading={isDeleting}
          disabled={!analysis || soleOwned.length > 0}
        >
          Delete account
        </Button>
      </div>

      {movingTable && (
        <MoveTableDialog
          tableId={movingTable.table_id}
          tableName={movingTable.table_name}
          open={true}
          onOpenChange={(next) => {
            if (!next) {
              setMovingTable(null);
              void refresh();
            }
          }}
        />
      )}
    </>
  );
};
